import { useCallback, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import { ClioService, LloFunnelReportItem, LloFunnelReportRequest, OpenAPI } from "../../../api";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { LloFunnelFilters } from "./LloFunnel/LloFunnelFilters";
import { LloFunnelTable } from "./LloFunnel/LloFunnelTable";
import { applyLloFunnelFilters, uniqueFilterOptions } from "./LloFunnel/filtering";
import { computeCaseGroupSpans, groupRowsByCase, sortReportRows } from "./LloFunnel/sorting";
import {
  EMPTY_FILTERS,
  type FilterFieldKey,
  type LloFunnelFiltersState,
  type SortableColumn,
  type SortDirection,
} from "./LloFunnel/types";

const MySwal = withReactContent(Swal);

const FILTER_OPTION_FIELDS: FilterFieldKey[] = [
  "caseName",
  "raa",
  "ap",
  "lloExpiration",
  "negotiation",
  "funnel",
  "taskName",
  "taskAssignee",
];

export default function LloFunnelReport() {
  const [reportData, setReportData] = useState<LloFunnelReportItem[]>([]);
  const [filters, setFilters] = useState<LloFunnelFiltersState>(EMPTY_FILTERS);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<SortDirection>("asc");
  const [orderBy, setOrderBy] = useState<SortableColumn>("caseName");

  const buildRequest = useCallback((): LloFunnelReportRequest => {
    return {
      caseName: filters.caseName || null,
      raa: filters.raa || null,
      ap: filters.ap || null,
      lloExpiration: filters.lloExpiration || null,
      negotiation: filters.negotiation || null,
      funnel: filters.funnel || null,
      taskName: filters.taskName || null,
      taskAssignee: filters.taskAssignee || null,
      startDate: filters.dueDateFrom ? dayjs(filters.dueDateFrom).format("YYYY-MM-DD") : null,
      endDate: filters.dueDateTo ? dayjs(filters.dueDateTo).format("YYYY-MM-DD") : null,
    };
  }, [filters]);

  const loadReport = useCallback(() => {
    setLoading(true);
    // Load the full dataset so filter dropdowns keep all options; UI filters apply client-side.
    ClioService.postApiClioLloFunnelReport({})
      .then((data) => {
        setReportData(data);
        setPage(0);
      })
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error loading LLO funnel report.</p>,
        })
      )
      .finally(() => setLoading(false));
  }, []);

  const filterOptions = useMemo(() => {
    const options = {} as Record<FilterFieldKey, string[]>;
    FILTER_OPTION_FIELDS.forEach((field) => {
      options[field] = uniqueFilterOptions(reportData, field);
    });
    return options;
  }, [reportData]);

  const filteredData = useMemo(
    () => applyLloFunnelFilters(reportData, filters),
    [reportData, filters]
  );

  const sortedData = useMemo(
    () => sortReportRows(filteredData, orderBy, order),
    [filteredData, order, orderBy]
  );

  const caseGroups = useMemo(() => groupRowsByCase(sortedData), [sortedData]);

  const paginatedRows = useMemo(() => {
    const pageGroups = caseGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    return computeCaseGroupSpans(pageGroups.flat());
  }, [caseGroups, page, rowsPerPage]);

  const handleFiltersChange = (next: LloFunnelFiltersState) => {
    setFilters(next);
    setPage(0);
  };

  const handleRequestSort = (column: SortableColumn) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
    setPage(0);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${OpenAPI.BASE}/api/Clio/lloFunnelReport/download`, {
        method: "POST",
        headers: {
          Authorization: (OpenAPI.HEADERS as Record<string, string>)?.Authorization,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(buildRequest()),
      });

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `LloFunnelReport-${dayjs().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      MySwal.fire({
        icon: "error",
        title: <p>Error downloading LLO funnel report.</p>,
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "calc(100vh - 72px)",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        overflow: "hidden",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant="h5" fontWeight={600} lineHeight={1.2}>
          LLO Case Funnel Overview
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          Limited Litigation tasks with descriptions modified from the standard template.
        </Typography>
      </Box>

      <LloFunnelFilters
        filters={filters}
        filterOptions={filterOptions}
        orderBy={orderBy}
        order={order}
        loading={loading}
        downloading={downloading}
        canDownload={filteredData.length > 0}
        onFiltersChange={handleFiltersChange}
        onOrderByChange={(column) => {
          setOrderBy(column);
          setPage(0);
        }}
        onOrderChange={(direction) => {
          setOrder(direction);
          setPage(0);
        }}
        onLoad={loadReport}
        onDownload={handleDownload}
        onClear={() => handleFiltersChange({ ...EMPTY_FILTERS })}
      />

      <LloFunnelTable
        loading={loading}
        hasLoadedData={reportData.length > 0}
        uniqueCaseCount={caseGroups.length}
        taskCount={filteredData.length}
        totalTaskCount={reportData.length}
        rows={paginatedRows}
        caseGroupCount={caseGroups.length}
        page={page}
        rowsPerPage={rowsPerPage}
        order={order}
        orderBy={orderBy}
        onRequestSort={handleRequestSort}
        onPageChange={setPage}
        onRowsPerPageChange={(next) => {
          setRowsPerPage(next);
          setPage(0);
        }}
      />
    </Box>
  );
}
